import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { BookOpen, Award, CheckCircle, Sparkles, TrendingUp, Shield, Zap } from 'lucide-react';
export function PeptideMasteryDetailSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05
  });
  return <section className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-black via-purple-950/20 to-black overflow-hidden" data-id="element-3275">
      {/* Mystical background effects - matching existing sections */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                         radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)`
    }} data-id="element-3276" />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/3 w-64 h-64 sm:w-96 sm:h-96 bg-purple-600/10 rounded-full blur-2xl sm:blur-3xl animate-pulse-slow" data-id="element-3277" />
      <div className="absolute bottom-1/4 right-1/3 w-64 h-64 sm:w-96 sm:h-96 bg-blue-600/10 rounded-full blur-2xl sm:blur-3xl animate-pulse-slow" style={{
      animationDelay: '1s'
    }} data-id="element-3278" />

      <div ref={ref} className="relative z-10 max-w-5xl mx-auto" data-id="element-3279">
        {/* Main Headline */}
        <h2 className="text-3xl sm:text-4xl md:text-6xl font-serif text-center mb-8 sm:mb-12 leading-tight text-stone-100" style={{
        fontFamily: 'Cinzel, serif',
        textShadow: '0 0 30px rgba(168, 85, 247, 0.4)'
      }} data-id="element-3280">
          Here's Exactly What You're Getting When You Join{' '}
          <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent" style={{
          textShadow: '0 0 40px rgba(168, 85, 247, 0.6)'
        }} data-id="element-3281">
            PSI PRO
          </span>
          :
        </h2>

        <p className="text-lg sm:text-xl md:text-2xl text-stone-300 text-center mb-10 sm:mb-16" data-id="element-3282">
          Let me break down the{' '}
          <span className="text-amber-400 font-bold" data-id="element-3283">COMPLETE</span> package...
        </p>

        {/* PEPTIDE MASTERY 2.0 - MAIN SECTION */}
        <div className="relative mb-12 sm:mb-20" data-id="element-3284">
          <div className="absolute inset-0 bg-gradient-radial from-amber-600/20 via-transparent to-transparent blur-3xl" data-id="element-3285" />

          <div className="relative bg-gradient-to-br from-amber-950/60 via-stone-950/80 to-black border-4 border-amber-600/60 p-6 sm:p-10 md:p-12 rounded-lg shadow-[0_0_60px_rgba(217,119,6,0.5)]" data-id="element-3286">
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3287">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-3288" />
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-3289" />
            </div>
            <div className="absolute top-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3290">
              <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-3291" />
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-3292" />
            </div>
            <div className="absolute bottom-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3293">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-3294" />
              <div className="absolute bottom-0 left-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-3295" />
            </div>
            <div className="absolute bottom-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3296">
              <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-3297" />
              <div className="absolute bottom-0 right-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-3298" />
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-6 sm:mb-8" data-id="element-3299">
              <div className="relative" data-id="element-3300">
                <div className="absolute inset-0 bg-amber-500/40 blur-xl animate-pulse-slow" data-id="element-3301" />
                <CheckCircle className="relative w-12 h-12 sm:w-16 sm:h-16 text-amber-400 drop-shadow-[0_0_15px_rgba(251,191,36,0.8)]" data-id="element-3302" />
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-6 sm:mb-8" data-id="element-3303">
              <h3 className="text-2xl sm:text-4xl md:text-5xl font-serif font-bold text-amber-400 mb-4" style={{
              fontFamily: 'Cinzel, serif',
              textShadow: '0 0 40px rgba(217, 119, 6, 0.6)'
            }} data-id="element-3304">
                PEPTIDE MASTERY 2.0 — THE COMPLETE BIOLOGICAL OPTIMIZATION
                BLUEPRINT
              </h3>

              <div className="inline-block px-4 sm:px-6 py-2 sm:py-3 bg-green-600/30 border-2 border-green-500/50 rounded-lg mt-4" data-id="element-3305">
                <p className="text-green-400 font-bold text-lg sm:text-xl" data-id="element-3306">
                  Included FREE when you join PSI PRO.
                </p>
              </div>

              <p className="text-stone-400 text-base sm:text-lg mt-4" data-id="element-3307">
                (Normally $2,500 — pay-in-full only, no payment plans)
              </p>
            </div>

            {/* Description */}
            <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-stone-300 mb-8 sm:mb-12" data-id="element-3308">
              <p className="text-lg sm:text-xl text-stone-100 font-semibold text-center" data-id="element-3309">
                This is the most comprehensive, scientifically validated,
                in-depth peptide training ever created.
              </p>

              <div className="bg-amber-950/30 border-l-4 border-amber-500 p-4 sm:p-6 space-y-3" data-id="element-3310">
                <p className="text-stone-100" data-id="element-3311">
                  The same knowledge base used when{' '}
                  <span className="font-bold text-amber-400" data-id="element-3312">
                    $120,000/year private clients
                  </span>{' '}
                  come to Brenden with "impossible" cases.
                </p>
                <p className="text-stone-100" data-id="element-3313">
                  The same protocols implemented when{' '}
                  <span className="font-bold text-amber-400" data-id="element-3314">
                    Olympic athletes
                  </span>{' '}
                  need an edge.
                </p>
                <p className="text-stone-100" data-id="element-3315">
                  The same frameworks applied when{' '}
                  <span className="font-bold text-amber-400" data-id="element-3316">
                    doctors send him their toughest cases
                  </span>
                  .
                </p>
              </div>

              <p className="text-xl sm:text-2xl font-bold text-center text-amber-400 mt-6 sm:mt-8" data-id="element-3317">
                Here's what's inside:
              </p>
            </div>

            {/* 130+ PEPTIDES SECTION */}
            <div className="mb-10 sm:mb-16" data-id="element-3318">
              <div className="bg-gradient-to-br from-purple-950/40 to-black border-2 border-purple-500/50 p-6 sm:p-8 rounded-lg" data-id="element-3319">
                <div className="flex items-center gap-4 mb-4 sm:mb-6" data-id="element-3320">
                  <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-purple-400" data-id="element-3321" />
                  <h4 className="text-2xl sm:text-3xl font-bold text-purple-400" data-id="element-3322">
                    130+ PEPTIDES DECODED AT THE MOLECULAR LEVEL
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-3323">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-3324">
                    Complete modules on over 130 powerful peptides — including
                    rare ones most "experts" have never heard of (and never will
                    unless they go to the source like Brenden has).
                  </p>

                  <p className="text-amber-400 font-bold" data-id="element-3325">
                    This is the ONLY course in the world that truly covers all
                    of Khavinson's peptides.
                  </p>

                  <p className="text-stone-100 font-semibold" data-id="element-3326">
                    Not summaries. Not overviews.
                  </p>

                  <p className="text-lg sm:text-xl text-stone-100 font-bold mb-4" data-id="element-3327">
                    Deep mechanistic breakdowns of:
                  </p>

                  <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-purple-500/30 text-sm sm:text-base" data-id="element-3328">
                    <p className="flex items-start gap-3" data-id="element-3329">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-3330" />
                      <span data-id="element-3331">
                        <span className="font-bold text-purple-400" data-id="element-3332">
                          Exact mechanisms of action
                        </span>{' '}
                        (what it actually does in your body at the cellular and
                        molecular level)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-3333">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-3334" />
                      <span data-id="element-3335">
                        <span className="font-bold text-purple-400" data-id="element-3336">
                          Optimal dosing protocols
                        </span>{' '}
                        (the doses that actually work, based on real research
                        and real results)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-3337">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-3338" />
                      <span data-id="element-3339">
                        <span className="font-bold text-purple-400" data-id="element-3340">
                          Synergistic combinations
                        </span>{' '}
                        (how to stack peptides for exponential rather than
                        additive effects)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-3341">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-3342" />
                      <span data-id="element-3343">
                        <span className="font-bold text-purple-400" data-id="element-3344">
                          Timing and absorption optimization
                        </span>{' '}
                        (when to dose for maximum bioavailability)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-3345">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-3346" />
                      <span data-id="element-3347">
                        <span className="font-bold text-purple-400" data-id="element-3348">
                          Safety considerations
                        </span>{' '}
                        (real risks based on actual science, not internet
                        fear-mongering)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-3349">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-3350" />
                      <span data-id="element-3351">
                        <span className="font-bold text-purple-400" data-id="element-3352">
                          Product type identification
                        </span>{' '}
                        (how to tell if you're buying Type 1, 2, or 3
                        bioregulators)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-3353">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-3354" />
                      <span data-id="element-3355">
                        <span className="font-bold text-purple-400" data-id="element-3356">
                          Where to source high-quality versions
                        </span>{' '}
                        (because knowing what to take is worthless if what
                        you're taking is bunk)
                      </span>
                    </p>
                  </div>

                  <div className="bg-purple-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-3357">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold" data-id="element-3358">
                      This goes far beyond what you'll find in any other course,
                      book, or training program.
                    </p>
                    <p className="text-purple-400 font-semibold mt-2 text-sm sm:text-base" data-id="element-3359">
                      Because no other program was created by someone who's
                      actually gone to the source.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 54 PROTOCOLS SECTION */}
            <div className="mb-10 sm:mb-16" data-id="element-3360">
              <div className="bg-gradient-to-br from-cyan-950/40 to-black border-2 border-cyan-500/50 p-6 sm:p-8 rounded-lg" data-id="element-3361">
                <div className="flex items-center gap-4 mb-4 sm:mb-6" data-id="element-3362">
                  <Zap className="w-8 h-8 sm:w-10 sm:h-10 text-cyan-400" data-id="element-3363" />
                  <h4 className="text-2xl sm:text-3xl font-bold text-cyan-400" data-id="element-3364">
                    54 BRENDEN HENRY PROTOCOLS
                  </h4>
                </div>

                <p className="text-xl sm:text-2xl text-cyan-300 font-bold mb-6" data-id="element-3365">
                  Precision-Engineered Synergistic Protocols That Create
                  "Impossible" Transformations
                </p>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-3366">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-3367">
                    This is where theory becomes transformation.
                  </p>

                  <p data-id="element-3368">
                    These aren't generic templates you could find with enough
                    Google searching.
                  </p>
                  <p data-id="element-3369">These aren't "here's what might work" suggestions.</p>

                  <p className="text-lg sm:text-xl text-cyan-400 font-bold" data-id="element-3370">
                    These are precision-engineered protocols Brenden has
                    developed through:
                  </p>

                  <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-cyan-500/30 text-sm sm:text-base" data-id="element-3371">
                    <p className="flex items-start gap-3" data-id="element-3372">
                      <span className="text-cyan-400 flex-shrink-0" data-id="element-3373">→</span>
                      <span data-id="element-3374">
                        Years of translating original Russian research
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-3375">
                      <span className="text-cyan-400 flex-shrink-0" data-id="element-3376">→</span>
                      <span data-id="element-3377">
                        Extensive experimentation with 1,500+ participants
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-3378">
                      <span className="text-cyan-400 flex-shrink-0" data-id="element-3379">→</span>
                      <span data-id="element-3380">
                        Real-world application with private $15,000−$120,000
                        transformation clients
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-3381">
                      <span className="text-cyan-400 flex-shrink-0" data-id="element-3382">→</span>
                      <span data-id="element-3383">
                        Deep mechanistic understanding of peptide synergies
                        nobody else knows
                      </span>
                    </p>
                  </div>

                  <div className="bg-cyan-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-3384">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-4" data-id="element-3385">
                      Each protocol shows you:
                    </p>
                    <div className="space-y-2 pl-4 text-sm sm:text-base" data-id="element-3386">
                      <p className="flex items-start gap-3" data-id="element-3387">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-3388" />
                        <span data-id="element-3389">Exactly what peptides to use</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-3390">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-3391" />
                        <span data-id="element-3392">Precise dosing schedules</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-3393">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-3394" />
                        <span data-id="element-3395">How to stack them for maximum synergy</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-3396">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-3397" />
                        <span data-id="element-3398">Expected timeline for results</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-3399">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-3400" />
                        <span data-id="element-3401">How to source everything you need</span>
                      </p>
                    </div>
                  </div>

                  {/* Protocol Categories */}
                  <div className="mt-8" data-id="element-3402">
                    <p className="text-xl sm:text-2xl font-bold text-cyan-400 mb-6 text-center" data-id="element-3403">
                      Here are all 54 protocols you're getting:
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" data-id="element-3404">
                      {/* REGENERATION & HEALING */}
                      <div className="bg-emerald-950/30 border border-emerald-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3405">
                        <h5 className="text-lg sm:text-xl font-bold text-emerald-400 mb-4" data-id="element-3406">
                          REGENERATION & HEALING:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3407">
                          <li data-id="element-3408">• Liver Regeneration</li>
                          <li data-id="element-3409">• Kidney Repair</li>
                          <li data-id="element-3410">• Bone Healing</li>
                          <li data-id="element-3411">• Accelerated Healing</li>
                          <li data-id="element-3412">• Regenerate Spinal Discs</li>
                        </ul>
                      </div>

                      {/* NEUROLOGICAL & COGNITIVE */}
                      <div className="bg-purple-950/30 border border-purple-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3413">
                        <h5 className="text-lg sm:text-xl font-bold text-purple-400 mb-4" data-id="element-3414">
                          NEUROLOGICAL & COGNITIVE:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3415">
                          <li data-id="element-3416">• Traumatic Brain Injury Recovery</li>
                          <li data-id="element-3417">• Stroke Recovery</li>
                          <li data-id="element-3418">• Alzheimer's Disease Regeneration</li>
                          <li data-id="element-3419">• Parkinson's Disease Regeneration</li>
                          <li data-id="element-3420">• Cognitive Decline / Brain Fog Solution</li>
                          <li data-id="element-3421">
                            • Improve Speed of Learning and Memory Retention
                          </li>
                          <li data-id="element-3422">• ADHD Calm Focus Protocol</li>
                        </ul>
                      </div>

                      {/* MENTAL HEALTH & NEUROCHEMISTRY */}
                      <div className="bg-blue-950/30 border border-blue-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3423">
                        <h5 className="text-lg sm:text-xl font-bold text-blue-400 mb-4" data-id="element-3424">
                          MENTAL HEALTH & NEUROCHEMISTRY:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3425">
                          <li data-id="element-3426">• Anti-Anxiety</li>
                          <li data-id="element-3427">• Mood Enhancement and Depression Relief</li>
                          <li data-id="element-3428">• Anhedonia Fix & Pleasure Re-Sensitization</li>
                          <li data-id="element-3429">• Addiction Support Stack</li>
                        </ul>
                      </div>

                      {/* METABOLIC & ENDOCRINE */}
                      <div className="bg-amber-950/30 border border-amber-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3430">
                        <h5 className="text-lg sm:text-xl font-bold text-amber-400 mb-4" data-id="element-3431">
                          METABOLIC & ENDOCRINE:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3432">
                          <li data-id="element-3433">• Diabetes Control</li>
                          <li data-id="element-3434">• Fix Hypothyroidism</li>
                          <li data-id="element-3435">• Male Hormone Optimization</li>
                          <li data-id="element-3436">• Female Hormone Optimization</li>
                          <li data-id="element-3437">• Low Energy and Chronic Fatigue Fix</li>
                          <li data-id="element-3438">
                            • Stubborn Fat Loss and Muscle Gain Enhancement
                          </li>
                        </ul>
                      </div>

                      {/* CARDIOVASCULAR & CIRCULATORY */}
                      <div className="bg-red-950/30 border border-red-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3439">
                        <h5 className="text-lg sm:text-xl font-bold text-red-400 mb-4" data-id="element-3440">
                          CARDIOVASCULAR & CIRCULATORY:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3441">
                          <li data-id="element-3442">• Ultimate Heart Protocol</li>
                          <li data-id="element-3443">• Blood Vessel and Artery Support</li>
                          <li data-id="element-3444">• Anemia Fix</li>
                        </ul>
                      </div>

                      {/* IMMUNE & AUTOIMMUNE */}
                      <div className="bg-green-950/30 border border-green-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3445">
                        <h5 className="text-lg sm:text-xl font-bold text-green-400 mb-4" data-id="element-3446">
                          IMMUNE & AUTOIMMUNE:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3447">
                          <li data-id="element-3448">• Immune System Fortification</li>
                          <li data-id="element-3449">• Autoimmune Support</li>
                          <li data-id="element-3450">• Cancer Recovery Support</li>
                        </ul>
                      </div>

                      {/* ANTI-AGING & LONGEVITY */}
                      <div className="bg-pink-950/30 border border-pink-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3451">
                        <h5 className="text-lg sm:text-xl font-bold text-pink-400 mb-4" data-id="element-3452">
                          ANTI-AGING & LONGEVITY:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3453">
                          <li data-id="element-3454">• Biological Age Reversal</li>
                          <li data-id="element-3455">• Elderly Lifespan Stack</li>
                          <li data-id="element-3456">• Skin Rejuvenation</li>
                          <li data-id="element-3457">• Hair Growth and Health</li>
                        </ul>
                      </div>

                      {/* RESPIRATORY & ENVIRONMENTAL */}
                      <div className="bg-teal-950/30 border border-teal-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3458">
                        <h5 className="text-lg sm:text-xl font-bold text-teal-400 mb-4" data-id="element-3459">
                          RESPIRATORY & ENVIRONMENTAL:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3460">
                          <li data-id="element-3461">• Lung Power</li>
                          <li data-id="element-3462">• Ultimate Cold Tolerance</li>
                          <li data-id="element-3463">• Ultimate Anti-Hypoxiant</li>
                          <li data-id="element-3464">• "Hold Your Breath for Longer" Protocol</li>
                          <li data-id="element-3465">• Radiation Resilience and Recovery Regimen</li>
                        </ul>
                      </div>

                      {/* PERFORMANCE & ATHLETICS */}
                      <div className="bg-orange-950/30 border border-orange-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3466">
                        <h5 className="text-lg sm:text-xl font-bold text-orange-400 mb-4" data-id="element-3467">
                          PERFORMANCE & ATHLETICS:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3468">
                          <li data-id="element-3469">• Athletic Performance Enhancement</li>
                          <li data-id="element-3470">• Military Personnel Protocol</li>
                        </ul>
                      </div>

                      {/* PAIN & INFLAMMATION */}
                      <div className="bg-rose-950/30 border border-rose-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3471">
                        <h5 className="text-lg sm:text-xl font-bold text-rose-400 mb-4" data-id="element-3472">
                          PAIN & INFLAMMATION:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3473">
                          <li data-id="element-3474">• Chronic Pain Management</li>
                          <li data-id="element-3475">• Neuropathy Support</li>
                          <li data-id="element-3476">• Chronic Prostatitis Cure</li>
                        </ul>
                      </div>

                      {/* SENSORY & RESTORATION */}
                      <div className="bg-indigo-950/30 border border-indigo-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3477">
                        <h5 className="text-lg sm:text-xl font-bold text-indigo-400 mb-4" data-id="element-3478">
                          SENSORY & RESTORATION:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3479">
                          <li data-id="element-3480">• Eye Health and Vision Enhancement</li>
                          <li data-id="element-3481">• Reverse Hearing Loss</li>
                          <li data-id="element-3482">• Restore Olfaction</li>
                        </ul>
                      </div>

                      {/* DIGESTIVE & ORAL */}
                      <div className="bg-lime-950/30 border border-lime-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3483">
                        <h5 className="text-lg sm:text-xl font-bold text-lime-400 mb-4" data-id="element-3484">
                          DIGESTIVE & ORAL:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3485">
                          <li data-id="element-3486">• Digestive Health</li>
                          <li data-id="element-3487">• Reverse Gum Disease and Periodontitis</li>
                        </ul>
                      </div>

                      {/* SLEEP & RECOVERY */}
                      <div className="bg-violet-950/30 border border-violet-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3488">
                        <h5 className="text-lg sm:text-xl font-bold text-violet-400 mb-4" data-id="element-3489">
                          SLEEP & RECOVERY:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3490">
                          <li data-id="element-3491">• Sleep Optimization</li>
                        </ul>
                      </div>

                      {/* QUALITY OF LIFE */}
                      <div className="bg-fuchsia-950/30 border border-fuchsia-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3492">
                        <h5 className="text-lg sm:text-xl font-bold text-fuchsia-400 mb-4" data-id="element-3493">
                          QUALITY OF LIFE:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3494">
                          <li data-id="element-3495">• Supercharged Libido Stack</li>
                          <li data-id="element-3496">• Enhanced Tanning Protocol</li>
                          <li data-id="element-3497">• Pregnancy Symptom Relief</li>
                        </ul>
                      </div>

                      {/* RARE CONDITIONS */}
                      <div className="bg-sky-950/30 border border-sky-500/30 p-4 sm:p-6 rounded-lg" data-id="element-3498">
                        <h5 className="text-lg sm:text-xl font-bold text-sky-400 mb-4" data-id="element-3499">
                          RARE CONDITIONS:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-3500">
                          <li data-id="element-3501">• Huntington's Disease Recovery</li>
                          <li data-id="element-3502">
                            • Duchenne Muscular Dystrophy or Spinal-Bulbar
                            Muscular Atrophy Healing
                          </li>
                          <li data-id="element-3503">• Sarcopenia Recovery</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-cyan-950/30 p-4 sm:p-6 rounded-lg mt-8" data-id="element-3504">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-2" data-id="element-3505">
                      Each one is a complete roadmap.
                    </p>
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-2" data-id="element-3506">
                      Each one is based on years of research and real human
                      results.
                    </p>
                    <p className="text-lg sm:text-xl text-stone-100 font-bold" data-id="element-3507">
                      Each one gives you exactly what you need to implement it
                      successfully.
                    </p>
                    <p className="text-xl sm:text-2xl text-cyan-400 font-bold mt-6" data-id="element-3508">
                      Having ONE protocol that actually works for YOUR specific
                      situation is worth more than the entire investment.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 1,193 SCIENTIFIC CITATIONS */}
            <div className="mb-10 sm:mb-16" data-id="element-3509">
              <div className="bg-gradient-to-br from-blue-950/40 to-black border-2 border-blue-500/50 p-6 sm:p-8 rounded-lg" data-id="element-3510">
                <div className="flex items-center gap-4 mb-4 sm:mb-6" data-id="element-3511">
                  <BookOpen className="w-8 h-8 sm:w-10 sm:h-10 text-blue-400" data-id="element-3512" />
                  <h4 className="text-2xl sm:text-3xl font-bold text-blue-400" data-id="element-3513">
                    BACKED WITH 1,193 SCIENTIFIC CITATIONS
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-3514">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-3515">
                    Every claim is backed by peer-reviewed research.
                  </p>

                  <p data-id="element-3516">All available in the Master Citation List.</p>

                  <p className="text-blue-400 font-bold" data-id="element-3517">
                    We sell this citation list separately for $1,193 (one dollar
                    per citation).
                  </p>

                  <p className="text-lg sm:text-xl text-stone-100 font-bold" data-id="element-3518">
                    But it's included inside Peptide Mastery for free.
                  </p>

                  <div className="bg-blue-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-3519">
                    <p className="text-stone-100 mb-2" data-id="element-3520">
                      This isn't bro-science.
                    </p>
                    <p className="text-stone-100 mb-2" data-id="element-3521">
                      This isn't anecdotal evidence.
                    </p>
                    <p className="text-stone-100 mb-4" data-id="element-3522">
                      This isn't "I read on Reddit that..."
                    </p>
                    <p className="text-lg sm:text-xl text-blue-400 font-bold" data-id="element-3523">
                      This is decades of scientific validation, centralized and
                      simplified into the most valuable biological enhancement
                      resource in the world.
                    </p>
                  </div>

                  <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-blue-500/30 mt-6 text-sm sm:text-base" data-id="element-3524">
                    <p data-id="element-3525">
                      When Brenden makes a claim, it's backed by original
                      research.
                    </p>
                    <p data-id="element-3526">
                      When he recommends a protocol, it's validated by studies
                      most people don't even know exist.
                    </p>
                    <p data-id="element-3527">
                      When he states a mechanism, it's supported by
                      peer-reviewed science.
                    </p>
                  </div>

                  <p className="text-xl sm:text-2xl text-center text-stone-100 font-bold mt-6" data-id="element-3528">
                    You're not getting opinions.
                  </p>
                  <p className="text-xl sm:text-2xl text-center text-blue-400 font-bold" data-id="element-3529">
                    You're getting facts.
                  </p>
                </div>
              </div>
            </div>

            {/* MASS SPECTROMETRY VERIFICATION */}
            <div className="mb-10 sm:mb-16" data-id="element-3530">
              <div className="bg-gradient-to-br from-emerald-950/40 to-black border-2 border-emerald-500/50 p-6 sm:p-8 rounded-lg" data-id="element-3531">
                <div className="flex items-center gap-4 mb-4 sm:mb-6" data-id="element-3532">
                  <Shield className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-400" data-id="element-3533" />
                  <h4 className="text-2xl sm:text-3xl font-bold text-emerald-400" data-id="element-3534">
                    MASS SPECTROMETRY VERIFICATION
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-3535">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-3536">
                    We don't just tell you what to take.
                  </p>

                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-3537">
                    We show you PROOF that what you're buying is actually what
                    the label claims.
                  </p>

                  <div className="bg-red-950/30 border-l-4 border-red-500 p-4 sm:p-6 my-6" data-id="element-3538">
                    <p className="text-lg sm:text-xl text-red-400 font-bold mb-4" data-id="element-3539">
                      Because here's a dirty secret of the peptide industry:
                    </p>
                    <div className="space-y-2 text-stone-300 text-sm sm:text-base" data-id="element-3540">
                      <p data-id="element-3541">Not all peptides are created equal.</p>
                      <p data-id="element-3542">Some vendors are selling underdosed products.</p>
                      <p data-id="element-3543">Some are selling completely bunk products.</p>
                      <p data-id="element-3544">Some are selling contaminated products.</p>
                    </div>
                  </div>

                  <p className="text-stone-100 font-semibold" data-id="element-3545">
                    And you'd never know unless you had access to mass
                    spectrometry testing.
                  </p>

                  <p className="text-lg sm:text-xl text-emerald-400 font-bold" data-id="element-3546">
                    Which you now do.
                  </p>

                  <div className="bg-emerald-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-3547">
                    <p className="text-stone-100 mb-2 text-sm sm:text-base" data-id="element-3548">
                      <span className="font-bold text-emerald-400" data-id="element-3549">
                        Included in the course:
                      </span>{' '}
                      Mass spec analysis of popular peptide products, so you can
                      see exactly what's real and what's fake.
                    </p>
                    <p className="text-lg sm:text-xl text-emerald-400 font-bold mt-4" data-id="element-3550">
                      This alone has saved our clients thousands of dollars in
                      wasted purchases.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* LIFETIME UPDATES */}
            <div className="mb-10 sm:mb-16" data-id="element-3551">
              <div className="bg-gradient-to-br from-purple-950/40 to-black border-2 border-purple-500/50 p-6 sm:p-8 rounded-lg" data-id="element-3552">
                <div className="flex items-center gap-4 mb-4 sm:mb-6" data-id="element-3553">
                  <TrendingUp className="w-8 h-8 sm:w-10 sm:h-10 text-purple-400" data-id="element-3554" />
                  <h4 className="text-2xl sm:text-3xl font-bold text-purple-400" data-id="element-3555">
                    LIFETIME UPDATES
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-3556">
                  <p className="text-stone-100" data-id="element-3557">
                    The course has already been updated multiple times since its
                    original release.
                  </p>

                  <p className="text-lg sm:text-xl text-purple-400 font-bold" data-id="element-3558">
                    It's now 10x bigger than when it launched.
                  </p>

                  <p className="text-lg sm:text-xl text-stone-100 font-bold" data-id="element-3559">
                    And you get access to EVERY future update, forever.
                  </p>

                  <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-purple-500/30 mt-6 text-sm sm:text-base" data-id="element-3560">
                    <p data-id="element-3561">As new research emerges...</p>
                    <p data-id="element-3562">
                      As Brenden refines protocols based on new client
                      results...
                    </p>
                    <p data-id="element-3563">
                      As the science advances and new peptides become
                      available...
                    </p>
                  </div>

                  <p className="text-xl sm:text-2xl text-center text-purple-400 font-bold mt-6" data-id="element-3564">
                    You stay at the forefront.
                  </p>
                </div>
              </div>
            </div>

            {/* 12-MONTH TRANSFORMATION JOURNEY */}
            <div className="mb-8 sm:mb-12" data-id="element-3565">
              <div className="bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-500/50 p-6 sm:p-8 rounded-lg" data-id="element-3566">
                <div className="flex items-center gap-4 mb-4 sm:mb-6" data-id="element-3567">
                  <Award className="w-8 h-8 sm:w-10 sm:h-10 text-amber-400" data-id="element-3568" />
                  <h4 className="text-2xl sm:text-3xl font-bold text-amber-400" data-id="element-3569">
                    STRUCTURED AS A 12-MONTH TRANSFORMATION JOURNEY
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-3570">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-3571">
                    Peptide Mastery 2.0 is strategically sequenced as a 12-month
                    progression that takes you from foundational understanding
                    to complete mastery.
                  </p>

                  <p className="text-lg sm:text-xl text-amber-400 font-bold mb-4" data-id="element-3572">
                    Each month unlocks a new phase:
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3573">
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3574">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3575">Month 1:</p>
                      <p className="text-sm" data-id="element-3576">
                        Introduction & Basics + Mass Spectrometry Analysis
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3577">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3578">Month 2:</p>
                      <p className="text-sm" data-id="element-3579">
                        Sleep Optimization, Circadian Rhythm Repair & Stress
                        Adaptation
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3580">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3581">Month 3:</p>
                      <p className="text-sm" data-id="element-3582">
                        Gut Health, Digestive Repair & Detoxification
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3583">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3584">Month 4:</p>
                      <p className="text-sm" data-id="element-3585">
                        Brain Health, Cognitive Enhancement & Neuro-Regeneration
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3586">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3587">Month 5:</p>
                      <p className="text-sm" data-id="element-3588">
                        Cardiovascular Regeneration, Metabolic Health & Thyroid
                        Function
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3589">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3590">Month 6:</p>
                      <p className="text-sm" data-id="element-3591">
                        Joint, Bone & Cartilage Regeneration
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3592">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3593">Month 7:</p>
                      <p className="text-sm" data-id="element-3594">
                        Energy Production, Sports Performance & Endurance
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3595">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3596">Month 8:</p>
                      <p className="text-sm" data-id="element-3597">
                        Men's Hormonal Health, Vitality & Fertility
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3598">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3599">Month 9:</p>
                      <p className="text-sm" data-id="element-3600">
                        Women's Hormonal Balance, Thyroid & Reproductive Health
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3601">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3602">Month 10:</p>
                      <p className="text-sm" data-id="element-3603">
                        Injury Repair & Vision Restoration
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3604">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3605">Month 11:</p>
                      <p className="text-sm" data-id="element-3606">
                        Total Immune Defense, Respiratory Health & Infection
                        Control
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-3607">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-3608">Month 12:</p>
                      <p className="text-sm" data-id="element-3609">
                        Ultimate Longevity, Skin Rejuvenation & Detoxification
                      </p>
                    </div>
                  </div>

                  <div className="bg-amber-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-3610">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-4" data-id="element-3611">
                      This systematic approach ensures:
                    </p>
                    <div className="space-y-2 pl-4 text-sm sm:text-base" data-id="element-3612">
                      <p className="flex items-start gap-3" data-id="element-3613">
                        <span className="text-amber-400 flex-shrink-0" data-id="element-3614">→</span>
                        <span data-id="element-3615">You're not overwhelmed with information</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-3616">
                        <span className="text-amber-400 flex-shrink-0" data-id="element-3617">→</span>
                        <span data-id="element-3618">
                          You build comprehensive understanding methodically
                        </span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-3619">
                        <span className="text-amber-400 flex-shrink-0" data-id="element-3620">→</span>
                        <span data-id="element-3621">
                          You can implement protocols as you learn them
                        </span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-3622">
                        <span className="text-amber-400 flex-shrink-0" data-id="element-3623">→</span>
                        <span data-id="element-3624">
                          You develop true mastery rather than surface knowledge
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="text-center mt-8" data-id="element-3625">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-2" data-id="element-3626">
                      By the end of 12 months, you won't just have random
                      knowledge.
                    </p>
                    <p className="text-xl sm:text-2xl text-amber-400 font-bold" data-id="element-3627">
                      You'll have systematic mastery of biological optimization.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Final Summary */}
            <div className="text-center space-y-4 sm:space-y-6 mt-8 sm:mt-12" data-id="element-3628">
              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-100" data-id="element-3629">
                Again, that's{' '}
                <span className="text-amber-400" data-id="element-3630">Peptide Mastery 2.0</span>.
              </p>
              <p className="text-lg sm:text-xl text-stone-300" data-id="element-3631">
                Normally{' '}
                <span className="text-red-400 font-bold line-through" data-id="element-3632">
                  $2,500
                </span>{' '}
                (pay-in-full only).
              </p>
              <p className="text-2xl sm:text-3xl font-bold text-green-400" data-id="element-3633">
                But when you join PSI PRO, you get it INCLUDED FREE.
              </p>
            </div>
          </div>
        </div>

        {/* Transition to next section */}
        <div className="text-center mt-12 sm:mt-16" data-id="element-3634">
          <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-100 mb-4" data-id="element-3635">
            Now here's where this gets truly{' '}
            <span className="text-purple-400" data-id="element-3636">unprecedented</span>...
          </p>
        </div>
      </div>
    </section>;
}